import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { formatCalendarDate } from '@/lib/format';
import { cn } from '@/lib/utils';

const WIDTH = 600;
const HEIGHT = 200;
const Y_AXIS_WIDTH = 44;
const MAX_X_TICKS = 6;

export interface TrendSeries {
  key: string;
  label: string;
  color: string;
  /** One value per day, in the order of `days`. */
  values: number[];
}

/** Rounds a chart max up to a clean-looking number, e.g. 8,430 -> 9,000. */
function niceCeiling(value: number) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = magnitude / 2;
  return Math.ceil(value / step) * step;
}

function xOf(index: number, count: number) {
  // A single day has no span to spread across, so it sits in the middle.
  return count > 1 ? (index * WIDTH) / (count - 1) : WIDTH / 2;
}

function yOf(value: number, max: number) {
  return HEIGHT - (value / max) * HEIGHT;
}

function linePath(values: number[], max: number) {
  return values
    .map(
      (value, i) =>
        `${i === 0 ? 'M' : 'L'} ${xOf(i, values.length).toFixed(1)} ${yOf(value, max).toFixed(1)}`
    )
    .join(' ');
}

/**
 * One line per subject over the range, on one shared axis.
 *
 * The same hand-rolled SVG as the dashboard's revenue line, for the same
 * reason — a few series and a hover tooltip is all this needs — but with as
 * many series as there are subjects. `preserveAspectRatio="none"` stretches
 * the plot to the container; the axis text and the single-day dots are plain
 * DOM so that stretch never skews them.
 *
 * The legend sits above the plot and is always there once there are two
 * subjects: the third series colour is too light to carry identity alone, so
 * no line is ever left to be recognised by its colour without a name beside
 * it.
 */
export function UsageTrendChart({
  days,
  series,
  format,
  busy,
}: {
  /** `yyyy-MM-dd`, oldest first. */
  days: string[];
  series: TrendSeries[];
  format: (value: number) => string;
  busy?: boolean;
}) {
  const max = niceCeiling(
    Math.max(0, ...series.flatMap((entry) => entry.values))
  );
  const yTicks = [max, (max * 2) / 3, max / 3, 0];

  const tickCount = Math.min(MAX_X_TICKS, days.length);
  const xTickIndices =
    days.length <= 1
      ? [0]
      : Array.from({ length: tickCount }, (_, i) =>
          Math.round((i * (days.length - 1)) / (tickCount - 1))
        );

  return (
    <div className="bg-card relative rounded-xl border p-3">
      <BusyOverlay show={busy} />
      <div className={cn('space-y-3', busy && 'opacity-60 transition-opacity')}>
        {series.length > 1 && (
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {series.map((entry) => (
              <li key={entry.key} className="flex min-w-0 items-center gap-1.5">
                <span
                  aria-hidden
                  className="h-0.5 w-4 shrink-0 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="truncate">{entry.label}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-2">
          <div
            className="text-muted-foreground flex shrink-0 flex-col justify-between py-1 text-right text-xs tabular-nums"
            style={{ width: Y_AXIS_WIDTH }}
          >
            {yTicks.map((tick) => (
              <span key={tick}>{format(tick)}</span>
            ))}
          </div>

          <div className="relative h-56 min-w-0 flex-1">
            <svg
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              preserveAspectRatio="none"
              className="h-full w-full overflow-visible"
              aria-hidden
            >
              {yTicks.map((tick) => (
                <line
                  key={tick}
                  x1={0}
                  x2={WIDTH}
                  y1={yOf(tick, max)}
                  y2={yOf(tick, max)}
                  stroke="currentColor"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                  className="text-border"
                />
              ))}
              {days.length > 1 &&
                series.map((entry) => (
                  <path
                    key={entry.key}
                    d={linePath(entry.values, max)}
                    fill="none"
                    stroke={entry.color}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
            </svg>

            {/* One day draws no line, so it gets a marker instead. */}
            {days.length === 1 &&
              series.map((entry) => (
                <span
                  key={entry.key}
                  aria-hidden
                  className="ring-card absolute left-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2"
                  style={{
                    top: `${(yOf(entry.values[0] ?? 0, max) / HEIGHT) * 100}%`,
                    backgroundColor: entry.color,
                  }}
                />
              ))}

            {/* A column per day, wider than any mark, so the reader aims at a
                date and not at a two-pixel line. Each carries every series'
                value: comparing subjects on one day is the point of the page. */}
            <div className="absolute inset-0 flex">
              {days.map((day, index) => (
                <Tooltip key={day}>
                  <TooltipTrigger asChild>
                    <div className="group relative flex-1">
                      <span className="bg-foreground/25 absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 group-hover:block" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-semibold">{formatCalendarDate(day)}</p>
                    {series.map((entry) => (
                      <p key={entry.key} className="flex items-center gap-1.5">
                        <span
                          aria-hidden
                          className="size-2 shrink-0 rounded-full"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span>{entry.label}:</span>
                        <span className="ml-auto pl-2 tabular-nums">
                          {format(entry.values[index] ?? 0)}
                        </span>
                      </p>
                    ))}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>

        <div
          className="text-muted-foreground flex justify-between text-xs"
          style={{ paddingLeft: Y_AXIS_WIDTH + 8 }}
        >
          {xTickIndices.map((index) => (
            <span key={index}>{formatCalendarDate(days[index])}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
